<template lang="pug">
.ove-payment-view
  .loader-container(v-if="loading")
    b-spinner(variant="primary" label="Cargando...")

  template(v-else-if="oveDetail")
    .container-fluid.px-5.py-4
      // Header Section (Breadcrumbs + Title)
      .header-section.mb-4
        .header-nav.d-flex.align-items-center.gap-2.mb-1
          b-icon-arrow-left-square-fill.back-arrow(@click="$router.push(`/oportunidad-venta-equipo/${oveDetail.id}`)")
          h2.section-title.mb-0 Checkout Venta OVE
        b-breadcrumb.d-flex.justify-content-start(:items="breadcrumbItems")

      b-row
        // Columna Izquierda: Formulario
        b-col(lg="8")
          b-card.form-card.mb-3
            // Paso 1: Datos del Cliente
            .step-header.d-flex.align-items-center.mb-4
              .step-number 1
              h3.step-title Datos del Cliente

            .client-data-summary
              b-row.mb-3
                b-col(md="4")
                  .data-field
                    label.blue-label R.U.T
                    p {{ oveDetail.rut }}
                b-col(md="4")
                  .data-field
                    label Nombre
                    p {{ oveDetail.nombre }}
                b-col(md="4")
                  .data-field
                    label Apellido
                    p {{ oveDetail.apellido }}

              b-row.mb-3
                b-col(md="4")
                  .data-field
                    label Correo Electrónico
                    p {{ oveDetail.correo_cliente || oveDetail.correo_electronico }}
                b-col(md="4")
                  .data-field
                    label Teléfono
                    p {{ oveDetail.telefono }}
                b-col(md="4")
                  .data-field
                    label Persona de Contacto
                    p {{ oveDetail.persona_contacto || oveDetail.nombre_cliente }}

              b-row.mb-3
                b-col(md="4")
                  .data-field
                    label Región
                    p {{ oveDetail.region }}
                b-col(md="4")
                  .data-field
                    label Comuna
                    p {{ oveDetail.comuna }}
                b-col(md="4")
                  .data-field
                    label Dirección
                    p {{ oveDetail.direccion }}

            // Paso 2: Tipo de Documento
            .step-header.d-flex.align-items-center.mb-3.mt-5
              .step-number 2
              h3.step-title Tipo de Documento

            .invoice-toggle.mb-4
              p.text-muted.small Seleccione esta opción si necesitas Factura
              .d-flex.align-items-center
                b-form-checkbox(v-model="requiereFactura" size="lg")
                  span.ml-2 Factura

            // Sección Factura (Condicional)
            b-collapse(v-model="requiereFactura")
              .invoice-fields.bg-light.p-3.rounded.mb-5
                b-row.mb-3
                  b-col(md="8")
                    b-form-group(label="RUT Contribuyente" label-class="font-weight-bold")
                      .d-flex.gap-2
                        b-form-input(
                          v-model="form.rut_contribuyente"
                          placeholder="96123456-3"
                          @blur="formatRut('rut_contribuyente')"
                        )
                        b-button(
                          :variant="contribuyenteValidado ? 'danger' : (isValidRutContribuyente ? 'primary' : 'outline-secondary')"
                          @click="fetchContribuyenteAction"
                          :disabled="contribuyenteLoading || (!isValidRutContribuyente && !contribuyenteValidado)"
                        )
                          b-spinner(small v-if="contribuyenteLoading")
                          span(v-else) {{ contribuyenteValidado ? 'Limpiar' : 'Validar' }}
                    p.text-danger.small(v-if="contribuyenteError") {{ contribuyenteError }}

                b-row.mb-3(v-if="contribuyenteValidado")
                  b-col(md="12")
                    b-form-group(label="Razón Social" label-class="font-weight-bold")
                      b-form-input(v-model="form.razon_social" readonly disabled)

                b-row.mb-3(v-if="contribuyenteValidado")
                  b-col(md="12")
                    b-form-group(label="Giro" label-class="font-weight-bold")
                      b-form-select(
                        v-if="opcionesGiro.length > 1"
                        v-model="form.giro"
                        :options="opcionesGiro"
                      )
                      b-form-input(v-else v-model="form.giro" readonly disabled)
                b-row.mb-3(v-if="contribuyenteValidado")
                  b-col(md="12")
                    b-form-group(label="Dirección Tributaria" label-class="font-weight-bold")
                      b-form-select(
                        v-if="opcionesSucursal.length > 1"
                        :value="sucursalSeleccionadaIndex"
                        @change="onSucursalChange"
                      )
                        option(:value="null" disabled) Seleccione una dirección
                        option(
                          v-for="(op, i) in opcionesSucursal"
                          :key="i"
                          :value="i"
                        ) {{ op.label }}
                      b-form-input(v-else v-model="form.direccion_tributaria" readonly disabled)
                b-row.mb-3(v-if="contribuyenteValidado && form.comuna_tributaria")
                  b-col(md="12")
                    b-form-group(label="Comuna Tributaria" label-class="font-weight-bold")
                      b-form-input(v-model="form.comuna_tributaria" readonly disabled)

            // Paso 3: Garantía Extendida (Informativo en este flujo)
            .step-header.d-flex.align-items-center.mb-3.mt-4
              .step-number 3
              h3.step-title Garantía Extendida

            p.text-muted.small.mb-3 Garantías seleccionadas durante la cotización.
            .warranties-list
              .warranty-item-display(v-for="(item, idx) in oveDetail.detalles" :key="idx")
                .d-flex.align-items-center.gap-3.p-2.border.rounded.mb-2
                  b-icon(icon="shield-check" variant="success" font-scale="1.2")
                  div
                    p.mb-0.font-weight-bold {{ item.equipo_nombre }}
                    p.mb-0.small.text-muted(v-if="item.garantia_extendida") {{ item.garantia_nombre }} ({{ item.precio_garantia | formatCurrency }})
                    p.mb-0.small.text-muted(v-else) Sin garantía extendida adicional.

        // Columna Derecha: Resumen
        b-col(lg="4")
          .d-flex.flex-column.right-column
            // --- Card Producto ---
            .product-card.mb-3
              h4.font-weight-bold.mb-2 Producto
              .product-list
                .product-item-row(v-for="(item, idx) in oveDetail.detalles" :key="idx" :class="{'mt-3': idx > 0}")
                  .d-flex.align-items-center(style="gap: 12px")
                    .product-image-sm
                      img(v-if="item.imagen_url" :src="formatImgUrl(item.imagen_url)" alt="Producto")
                      b-icon(v-else icon="laptop" font-scale="1.5")
                    .flex-grow-1
                      p.small.text-muted.mb-1 {{ item.equipo_nombre }}
                      .d-flex.justify-content-between.align-items-center
                        span.small Cantidad 1
                        span.small.font-weight-bold.text-blue-accent {{ item.precio_unitario | formatCurrency }}

            // --- Card Resumen ---
            .totals-card
              h4.font-weight-bold.mb-3 Resumen

              // Bloque de cupón
              .cupon-group.mb-1
                .d-flex(style="gap: 8px")
                  b-form-input.cupon-input(
                    v-model="codigoCupon"
                    placeholder="Código de Descuento"
                    :disabled="cuponLoading || tieneCuponAplicado"
                    :readonly="tieneCuponAplicado"
                    @keyup.enter="onToggleCupon"
                  )
                  b-button.btn-cupon(
                    variant="samtek-yellow"
                    @click="onToggleCupon"
                    :disabled="cuponLoading || (!tieneCuponAplicado && !codigoCupon.trim())"
                  )
                    b-spinner(small v-if="cuponLoading")
                    span(v-else) {{ tieneCuponAplicado ? 'Eliminar' : 'Aplicar' }}

              // Mensaje éxito/error
              p.cupon-msg-success(v-if="tieneCuponAplicado") {{ cuponAplicado.mensaje }}
              p.cupon-msg-error(v-else-if="cuponError") {{ cuponError }}

              // Filas del resumen
              .d-flex.justify-content-between.mb-2.mt-2
                span.font-weight-bold Tipo Despacho:
                span {{ oveDetail.tipo_despacho === 'retiro' ? 'Retiro en Tienda' : 'Envío a Domicilio' }}
              .d-flex.justify-content-between.mb-2(v-if="oveDetail.tipo_despacho === 'envio'")
                span.font-weight-bold Dirección:
                span.text-right.small {{ oveDetail.direccion }}, {{ oveDetail.comuna }}
              .d-flex.justify-content-between.mb-2.mt-2
                span.font-weight-bold Sub-Total (neto):
                span {{ subtotal | formatCurrency }}
              .d-flex.justify-content-between.mb-2(v-if="manualDiscount > 0")
                span.font-weight-bold Descuento OVE (neto):
                span.descuento-manual-value -{{ manualDiscount | formatCurrency }}
              .d-flex.justify-content-between.mb-2
                span.font-weight-bold IVA (19%):
                span {{ iva | formatCurrency }}
              .d-flex.justify-content-between.mb-2(v-if="shippingCost > 0")
                span.font-weight-bold Despacho (IVA incl.):
                span {{ shippingCost | formatCurrency }}
              .d-flex.justify-content-between.mb-2(v-if="tieneCuponAplicado")
                span.font-weight-bold Cupón:
                span.descuento-value -{{ couponDiscount | formatCurrency }}

              hr

              .d-flex.justify-content-between.align-items-center.total-row.mb-3
                span TOTAL:
                span {{ totalFinal | formatCurrency }}

              b-button.btn-pay.w-100(
                variant="samtek-yellow"
                @click="modal = true"
                :disabled="submitting || (requiereFactura && (!contribuyenteValidado || !form.direccion_tributaria || !form.comuna_tributaria))"
              )
                b-spinner(small v-if="submitting")
                span(v-else) Marcar como Pagado

    SamtekModal(
      v-model="modal"
      confirmText="Confirmar"
      :loading="submitting"
      @confirm="confirmPayment"
      @cancel="modal = false"
    )
      template(#modal-title="")
        span.header-modal.text-center ¿Estás seguro de que quieres marcar el pago?

  .error-container(v-else-if="!loading")
    b-alert(show variant="danger")
    h4 OVE no encontrada
    b-button(variant="primary" @click="$router.push('/oportunidad-venta-equipo')") Volver al listado
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import validateRut from '@/utils/validateRut'
import { backUrl } from '../../../settings'

export default {
  name: 'OVEPaymentView',
  components: {
    SamtekModal: () => import('@/components/Common/SamtekModal.vue'),
  },
  data() {
    return {
      loading: true,
      submitting: false,
      modal: false,
      codigoCupon: '',
      form: {
        rut_contribuyente: '',
        razon_social: '',
        giro: '',
        direccion_tributaria: '',
        comuna_tributaria: '',
      },
      requiereFactura: false,
      contribuyenteValidado: false,
      contribuyenteInfo: null,
      sucursalSeleccionadaIndex: null,
      breadcrumbItems: [
        { text: 'Inicio', to: { path: '/' } },
        { text: 'OVE', to: { path: '/oportunidad-venta-equipo' } },
        {
          text: 'Detalle',
          to: {
            name: 'OVEDetailView',
            params: { ove_id: this.$route.params.ove_id },
          },
        },
        { text: 'Checkout Venta OVE', active: true },
      ],
    }
  },
  computed: {
    ...mapState('OVEStore', ['oveDetail']),
    ...mapState('VentaCenterStore', [
      'contribuyente',
      'contribuyenteLoading',
      'contribuyenteError',
      'cuponAplicado',
      'cuponLoading',
      'cuponError',
    ]),
    ...mapGetters('VentaCenterStore', [
      'tieneCuponAplicado',
      'porcentajeCupon',
    ]),

    // Desglose calculado en el backend (igual al detalle y al PDF):
    // Sub Total (neto) − Descuento (neto) + IVA + Envío (IVA incl.) = monto_total_final
    resumen() {
      return this.oveDetail?.resumen || {}
    },

    subtotal() {
      return this.resumen.subtotal_neto || 0
    },

    iva() {
      return this.resumen.iva || 0
    },

    shippingCost() {
      return this.resumen.envio || 0
    },

    manualDiscount() {
      return this.resumen.descuento_neto || 0
    },

    couponDiscount() {
      // El cupón se aplica sobre el total bruto pre-pago (monto_total_final)
      const base = this.oveDetail?.monto_total_final || 0
      return this.$store.getters['VentaCenterStore/montoCupon'](base)
    },

    totalFinal() {
      const base = this.oveDetail?.monto_total_final || 0
      return base - this.couponDiscount
    },

    isValidRutContribuyente() {
      return this.form.rut_contribuyente
        ? validateRut(this.form.rut_contribuyente)
        : null
    },

    opcionesGiro() {
      const c = this.contribuyenteInfo || {}
      const acts = c.actividades || []
      const vistos = new Set()
      const lista = []
      acts.forEach((a) => {
        const key = a.actividadEconomica || a.giro
        if (!key || vistos.has(key)) return
        vistos.add(key)
        lista.push(key)
      })
      if (!lista.length && c.giro) lista.push(c.giro)
      return lista
    },

    opcionesSucursal() {
      const c = this.contribuyenteInfo || {}
      const sucs = c.sucursales || []
      const vistas = new Set()
      const lista = []
      sucs.forEach((s) => {
        if (!s.direccion) return
        const key = `${s.direccion}|${s.comuna || ''}`
        if (vistas.has(key)) return
        vistas.add(key)
        lista.push({
          label: s.direccion,
          direccion: s.direccion,
          comuna: s.comuna || '',
        })
      })
      return lista
    },
  },
  watch: {
    requiereFactura(val) {
      if (!val) this.resetInvoiceFields()
    },
    contribuyente(val) {
      if (val) {
        this.contribuyenteInfo = val
        this.form.razon_social = val.razon_social || ''
        const acts = val.actividades || []
        const principal = acts.find((a) => a.actividadPrincipal) || acts[0]
        this.form.giro =
          principal?.actividadEconomica || principal?.giro || val.giro || ''
        this.form.direccion_tributaria = val.direccion || ''
        this.form.comuna_tributaria = val.comuna || ''
        const idx = this.opcionesSucursal.findIndex(
          (s) =>
            s.direccion === this.form.direccion_tributaria &&
            s.comuna === this.form.comuna_tributaria
        )
        this.sucursalSeleccionadaIndex = idx >= 0 ? idx : null
        this.contribuyenteValidado = true
      }
    },
  },
  methods: {
    ...mapActions('OVEStore', ['getOveDetail', 'confirmarPagoCenter']),
    ...mapActions('VentaCenterStore', [
      'fetchContribuyente',
      'validarCupon',
      'resetCupon',
    ]),

    async fetchData() {
      this.loading = true
      try {
        await this.getOveDetail(this.$route.params.ove_id)
      } catch (error) {
        console.error('Error fetching OVE:', error)
      } finally {
        this.loading = false
      }
    },

    formatImgUrl(url) {
      if (!url) return null
      if (url.startsWith('http')) return url
      return `${backUrl}${url}`
    },

    formatRut(field) {
      const rut = this.form[field].replace(/[^\dkK]/g, '')
      if (!rut) return
      const dv = rut.slice(-1)
      const num = rut.slice(0, -1)
      const formatted = `${num}-${dv}`
      this.form[field] = formatted.toUpperCase()
    },

    async fetchContribuyenteAction() {
      if (this.contribuyenteValidado) {
        this.resetInvoiceFields()
        return
      }
      await this.fetchContribuyente(this.form.rut_contribuyente)
    },

    resetInvoiceFields() {
      this.form.rut_contribuyente = ''
      this.form.razon_social = ''
      this.form.giro = ''
      this.form.direccion_tributaria = ''
      this.form.comuna_tributaria = ''
      this.contribuyenteValidado = false
      this.contribuyenteInfo = null
      this.sucursalSeleccionadaIndex = null
    },

    onSucursalChange(idx) {
      const op = this.opcionesSucursal[idx]
      if (op) {
        this.sucursalSeleccionadaIndex = idx
        this.form.direccion_tributaria = op.direccion
        this.form.comuna_tributaria = op.comuna
      }
    },

    async onToggleCupon() {
      if (this.tieneCuponAplicado) {
        this.resetCupon()
        this.codigoCupon = ''
        return
      }
      const codigo = this.codigoCupon.trim()
      if (!codigo) return
      await this.validarCupon(codigo)
      if (this.tieneCuponAplicado) {
        this.codigoCupon = this.cuponAplicado.codigo
      }
    },

    async confirmPayment() {
      this.submitting = true
      try {
        const payload = {
          ove_id: this.oveDetail.id,
          tipo_documento: this.requiereFactura ? 'factura' : 'boleta',
          rut_contribuyente: this.requiereFactura
            ? this.form.rut_contribuyente
            : '',
          razon_social_contribuyente: this.requiereFactura
            ? this.form.razon_social
            : '',
          giro_contribuyente: this.requiereFactura ? this.form.giro : '',
          direccion_tributaria_contribuyente: this.requiereFactura
            ? this.form.direccion_tributaria
            : '',
          comuna_tributaria_contribuyente: this.requiereFactura
            ? this.form.comuna_tributaria
            : '',
        }

        if (this.tieneCuponAplicado) {
          payload.codigo_cupon = this.cuponAplicado.codigo
        }

        await this.confirmarPagoCenter(payload)
        this.$bvToast.toast('Pago registrado exitosamente', {
          variant: 'success',
          title: 'Éxito',
        })
        this.$router.push(`/oportunidad-venta-equipo/${this.oveDetail.id}`)
      } catch (error) {
        console.error('Error al confirmar pago:', error)
        const msg = error.response?.data?.error || 'Error al registrar el pago'
        this.$bvToast.toast(msg, { variant: 'danger', title: 'Error' })
      } finally {
        this.submitting = false
      }
    },
  },
  async mounted() {
    await this.fetchData()
  },
  beforeDestroy() {
    this.resetCupon()
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

.ove-payment-view {
  background-color: #f5f6fa;
  min-height: 100vh;

  .loader-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
  }

  .header-section {
    margin-bottom: 1rem;

    .header-nav {
      display: flex;
      align-items: center;
      gap: 5px;
      margin-bottom: 0.25rem;
    }

    .section-title {
      font-size: 20px;
      font-weight: bold;
      color: #23201c;
    }
  }

  .back-arrow {
    color: $samtek-yellow;
    background-color: #23201c;
    border-radius: 6px;
    font-size: 1.5rem;
    cursor: pointer;
  }

  .form-card {
    border: none;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    padding: 10px;
  }

  .step-header {
    .step-number {
      background-color: #fece2f;
      color: #23201c;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 700;
      margin-right: 15px;
      flex-shrink: 0;
    }
    .step-title {
      font-family: 'Noto Sans', sans-serif;
      font-weight: 700;
      font-size: 1.25rem;
      margin-bottom: 0;
    }
  }

  .client-data-summary {
    .data-field {
      label {
        display: block;
        font-weight: 700;
        font-size: 14px;
        color: #525252;
        margin-bottom: 2px;
        &.blue-label {
          color: #1d4ed8;
        }
      }
      p {
        font-size: 15px;
        color: #525252;
        margin: 0;
        font-weight: 500;
      }
    }
  }

  .right-column {
    gap: 16px;
  }

  .product-card {
    background-color: #ffffff;
    border-radius: 10px;
    padding: 15px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

    .product-image-sm {
      width: 68px;
      height: 68px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #eee;
      border-radius: 4px;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }

  .totals-card {
    background-color: rgba(244, 194, 53, 0.1);
    border-radius: 10px;
    padding: 20px;

    .cupon-group {
      .cupon-input {
        flex: 1;
        height: 45px;
        border: 1px solid #525252;
        border-radius: 5px;
        font-size: 15px;
        color: #23201c;
        background-color: #ffffff;
        &:disabled {
          background-color: #f0f0f0;
          color: #6c757d;
        }
      }

      .btn-cupon {
        background-color: #fece2f;
        border: none;
        color: #23201c;
        font-weight: 500;
        height: 45px;
        border-radius: 8px;
        padding: 0 20px;
        &:hover:not(:disabled) {
          background-color: darken(#fece2f, 5%);
        }
      }
    }

    .cupon-msg-success {
      font-size: 10px;
      font-weight: 500;
      color: #1d4ed8;
      margin-top: 4px;
    }

    .cupon-msg-error {
      font-size: 10px;
      font-weight: 500;
      color: #ff3838;
      margin-top: 4px;
    }

    .descuento-manual-value {
      color: #525252;
      font-weight: 500;
    }

    .descuento-value {
      color: #1d4ed8;
      font-weight: 500;
    }

    .total-row {
      color: #1d4ed8;
      font-size: 20px;
      font-weight: 700;
    }

    .btn-pay {
      background-color: #fece2f;
      border: none;
      color: #23201c;
      font-weight: 500;
      height: 45px;
      border-radius: 8px;
      &:hover:not(:disabled) {
        background-color: darken(#fece2f, 5%);
      }
    }
  }

  .text-blue-accent {
    color: $blue-accent;
  }
}
</style>
